import type { FormData } from "@amzn/aquila-service-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "zustand";

import {
    BlinkWebsiteHost,
    PublisherWebsiteHost,
} from "../../../../config/auth.config";
import { authStore } from "../../../../store/auth";
import { navigationStore } from "../../../../store/navigation";
import { reportIssueStore } from "../../../../store/reportIssue";
import { salesforceStore } from "../../../../store/salesforce";
import { AquilaReportIssueTriggerType } from "../../../../types/aquila";
import { TopNavBarTabs } from "../../../../types/navigation";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import useEncodedUnifiedFilterStateUrl from "../../../misc/useEncodedUnifiedFilterStateUrl";

export const useReportIssueContextDataSync = (): {
    getReportIssueContextData: (
        trigger: AquilaReportIssueTriggerType,
    ) => FormData[];
} => {
    const [reportIssueContextData] = useStore(reportIssueStore, (state) => [
        state.contextData,
    ]);
    const [username, settings] = useStore(authStore, (state) => [
        state.username,
        state.settings,
    ]);
    const { domainId: domainIdPathParam = "" } = useParams();
    const [sfName] = useStore(salesforceStore, (state) => [
        state.sfContext.name,
    ]);
    const [activeTab] = useStore(navigationStore, (state) => [state.activeTab]);
    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );
    const [, getEncodedUnifiedFilterStateUrl] =
        useEncodedUnifiedFilterStateUrl();
    const encodedUnifiedFilterStateUrl = getEncodedUnifiedFilterStateUrl();
    const shareUrl = encodedUnifiedFilterStateUrl;

    useEffect(() => {
        const sfId = getAdvertiserId() ?? "";
        let context: { [key: string]: any } = {
            sfId,
            sfName,
            username,
            activeTab,
        };
        if (activeTab === TopNavBarTabs.ALL_INSIGHTS) {
            context = {
                ...context,
                shareUrl,
                domainIdPathParam,
            };
        }
        updateReportIssueContextData(context);
    }, [
        activeTab,
        sfName,
        domainIdPathParam,
        shareUrl,
        updateReportIssueContextData,
    ]);

    const getReportIssueContextData = useCallback(
        (trigger: AquilaReportIssueTriggerType) => {
            const commonData = [
                {
                    label: "Logged-in User",
                    value: username,
                    field: "loggedInUser",
                },
                {
                    label: "Tab Name",
                    value: (() => {
                        const urlParams = new URLSearchParams(
                            window.location.search,
                        );
                        const initialTab = urlParams.get("initialTab");
                        if (initialTab === "generated")
                            return TopNavBarTabs.YOUR_INSIGHT_DECKS;
                        if (initialTab === "insights")
                            return TopNavBarTabs.ALL_INSIGHTS;
                        return activeTab;
                    })(),
                    field: "activeTab",
                },
                {
                    label: "Salesforce Id",
                    value: getAdvertiserId() ?? "",
                    field: "sfId",
                },
                {
                    label: "Salesforce Name",
                    value: sfName ?? "",
                    field: "sfName",
                },
            ];
            const applicationUrl = [
                {
                    label: "Application Url",
                    value: shareUrl,
                    field: "applicationUrl",
                },
            ];
            const additionalData: FormData[] = [];
            const debugData: FormData[] = [];
            if (reportIssueContextData.apiError) {
                if (reportIssueContextData.apiError.requestUrl) {
                    debugData.push({
                        label: "Request URL",
                        value: reportIssueContextData.apiError.requestUrl,
                        field: "requestUrl",
                    });
                }
                if (reportIssueContextData.apiError.requestPayload) {
                    debugData.push({
                        label: "Request Payload",
                        value: reportIssueContextData.apiError.requestPayload,
                        field: "requestPayload",
                    });
                }
                if (reportIssueContextData.apiError.errorResponse) {
                    debugData.push({
                        label: "Error Response",
                        value: reportIssueContextData.apiError.errorResponse,
                        field: "errorResponse",
                    });
                }
                if (reportIssueContextData.apiError.errorCode) {
                    debugData.push({
                        label: "Error Code",
                        value: String(
                            reportIssueContextData.apiError.errorCode,
                        ),
                        field: "errorCode",
                    });
                }
            }

            if (
                [
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL,
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL,
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_CREATE_ON_DEMAND,
                ].includes(trigger)
            ) {
                if (reportIssueContextData.title) {
                    additionalData.push({
                        label: "Title",
                        value: reportIssueContextData.title,
                        field: "Title",
                    });
                }
                if (reportIssueContextData.identifier) {
                    additionalData.push({
                        label: "Identifier",
                        value: reportIssueContextData.identifier,
                        field: "identifier",
                    });
                }
            }

            if (
                trigger === AquilaReportIssueTriggerType.ALL_INSIGHTS_TAB ||
                trigger === AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL
            ) {
                if (
                    trigger ===
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL
                ) {
                    if (
                        reportIssueContextData.insightFamilyName &&
                        reportIssueContextData.insightType ===
                            InsightType.INSIGHT
                    ) {
                        additionalData.push({
                            label: "Insight Family Name",
                            value: reportIssueContextData.insightFamilyName,
                            field: "insightFamilyName",
                        });
                    }
                }
                return [
                    ...commonData,
                    ...additionalData,
                    ...applicationUrl,
                    ...debugData,
                ];
            } else if (
                trigger ===
                AquilaReportIssueTriggerType.ALL_INSIGHTS_CREATE_ON_DEMAND
            ) {
                const domainUrl = `https://${PublisherWebsiteHost[settings.stage]}/domains/${domainIdPathParam}`;
                return [
                    ...commonData,
                    {
                        label: "Domain Identifier",
                        value: domainUrl,
                        field: "domainIdentifier",
                    },
                    {
                        label: "Application Url",
                        value: window.location.href,
                        field: "applicationUrl",
                    },
                    ...debugData,
                ];
            } else if (
                trigger ===
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_TAB ||
                trigger ===
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL
            ) {
                const debugSpoofUrl = `https://${BlinkWebsiteHost[settings.stage]}?sfId=${getAdvertiserId() ?? ""}&supportMode=true&spoofAsUser=${username}`;
                const domainUrl = `https://${PublisherWebsiteHost[settings.stage]}/domains/${reportIssueContextData.domainIdentifier}`;
                const templateUrl = `${domainUrl}/templates/${reportIssueContextData.templateIdentifier}`;
                if (
                    trigger ===
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL
                ) {
                    if (reportIssueContextData.templateIdentifier) {
                        additionalData.push({
                            label: "Template Identifier",
                            value: templateUrl,
                            field: "templateIdentifier",
                        });
                    }
                    if (reportIssueContextData.domainIdentifier) {
                        additionalData.push({
                            label: "Domain Identifier",
                            value: domainUrl,
                            field: "domainIdentifier",
                        });
                    }
                }
                return [
                    ...commonData,
                    ...additionalData,
                    {
                        label: "Debug Spoof Url",
                        value: debugSpoofUrl,
                        field: "debugSpoofUrl",
                    },
                    ...debugData,
                ];
            } else {
                return [...commonData, ...debugData];
            }
        },
        [
            username,
            activeTab,
            sfName,
            domainIdPathParam,
            shareUrl,
            settings,
            reportIssueContextData,
        ],
    );

    return {
        getReportIssueContextData,
    };
};
